import React, { useEffect, useState } from 'react';
import { Dropdown } from '../../../../components/Dropdown';
import { IconSpan } from '../../../../components/IconSpan';
import classNames from 'classnames';
import styles from './styles.module.scss';
import {
  Application,
  ApplicationStatus,
} from '../../../../entities/Application';
import {
  ListScorecard,
  Scorecard,
} from '../../../../entities/applicant_tracking/Scorecard';
import { NoteModal } from '../../../../components/ApplicantTracking/NoteModal';
import { PropTypes as DropdownActionPropTypes } from '../../../../components/Dropdown/DropdownMenu/DropdownAction';
import { User } from '../../../../entities/User';
import { Chip, ChipColor } from '../../../../components/Chip';
import { appendRedirectUri } from '../../../../utils/url';
import { NoteService } from '../../../../services/applicant_tracking/NoteService';
import { ScorecardService } from '../../../../services/v1/applicant_tracking/ScorecardService';
import { AlertObject } from '../../../../components/Alert';
import { LegacyEditablePushToHrisModal } from '../LegacyEditablePushToHrisModal';
import { HrisProvider } from '../../../../enums/IntegrationProvider/HrisProvider';
import { getHrisName } from '../../../../utils/hris';
import { AddScorecardModal } from '../../FeedbackTab/AddFeedback/AddScorecardModal';
import { EditablePushToHrisModal } from '../EditablePushToHrisModal';
import { SendOfferSwitch } from '../../../applicant_tracking/SendOfferSwitch';
import { useFeatureFlags } from '../../../../contexts/FeatureFlagContext';
import {
  sendOfferButtonIcon,
  sendOfferButtonText,
} from '../../../../utils/applicant_tracking/offers';

interface PropTypes {
  application: Application;
  atsSignableOfferEnabled: boolean;
  disabled: boolean;
  sendEmailEnabled: boolean;
  newApplicationPermitted: boolean;
  sendOfferPermitted: boolean;
  user: User;
  offerRequisitionFlowEnabled: boolean;
  onClose: (succeeded: boolean) => void;
  onShareCNPSSurvey: (flag: boolean) => void;
  reloadApplication: () => void;
  setAlert: (alert: AlertObject) => void;
  setIsEmailModalOpen: (open: boolean) => void;
  setIsRejectModalOpen: (open: boolean) => void;
  setUnrejectModalOpen: (open: boolean) => void;
}

function renderSendOffer(
  onClick: () => void,
  sendOfferPermitted: boolean,
  offerRequisitionFlowEnabled: boolean,
): DropdownActionPropTypes {
  if (!sendOfferPermitted) return null;

  return {
    action: onClick,
    buttonChild: (
      <IconSpan
        spanText={sendOfferButtonText(offerRequisitionFlowEnabled)}
        icon={{
          name: sendOfferButtonIcon(offerRequisitionFlowEnabled),
          className: 'fs-5',
        }}
        className={'text-info'}
      />
    ),
  };
}

function viewOfferRequisition(offerRequisitionId: number) {
  if (!offerRequisitionId) return null;

  return {
    action: () =>
      (window.location.href = `/applicant_tracking/offer_requisitions/${offerRequisitionId}`),
    buttonChild: (
      <IconSpan
        spanText='View Offer Requisition'
        icon={{
          name: 'bi-file-medical',
          className: 'fs-4',
        }}
        className={'text-blue'}
      />
    ),
  };
}

function renderAddJobApplication(
  application: Application,
  newApplicationPermitted: boolean,
): DropdownActionPropTypes {
  if (!newApplicationPermitted || application.redacted) return null;

  return {
    action: () => {
      const url = new URL(
        '/applicant_tracking/applications/new',
        window.location.origin,
      );
      url.searchParams.append(
        'candidate_id',
        application.candidate.id.toString(),
      );
      window.location.href = appendRedirectUri(
        url.toString(),
        window.location.href,
        true,
      );
    },
    buttonChild: (
      <IconSpan
        spanText={'Add Job Application'}
        icon={{ name: 'bi-plus-circle', className: 'fs-5' }}
        className={'text-info'}
      />
    ),
  };
}

function renderEditJobApplication(
  application: Application,
  disabled: boolean,
): DropdownActionPropTypes {
  if (disabled || application.redacted) return null;

  return {
    action: () => {
      window.location.href = appendRedirectUri(
        `/applicant_tracking/applications/${application.id}/edit`,
        window.location.href,
        true,
      );
    },
    buttonChild: (
      <IconSpan
        spanText={'Edit Job Application'}
        icon={{ name: 'bi-pencil', className: 'fs-5' }}
        className={'text-info'}
      />
    ),
  };
}

function renderEditCandidateProfile(
  application: Application,
  disabled: boolean,
): DropdownActionPropTypes {
  if (disabled || application.redacted) return null;

  return {
    action: () => {
      window.location.href = appendRedirectUri(
        `/applicant_tracking/candidates/${application.candidate.id}/edit`,
        window.location.href,
        true,
      );
    },
    buttonChild: (
      <IconSpan
        spanText={'Edit Candidate Profile'}
        icon={{ name: 'bi-person', className: 'fs-5' }}
        className={'text-info'}
      />
    ),
  };
}

function FillInScorecard(props: { scorecardSize: number }) {
  if (props.scorecardSize <= 1) {
    return <span>Fill in scorecard</span>;
  }

  return (
    <span>
      <span>Fill in scorecard</span>
      <Chip className='ms-3' color={ChipColor.Danger}>
        {props.scorecardSize}
      </Chip>
    </span>
  );
}

function viewScorecards(
  application: Application,
  totalScorecards?: number,
): DropdownActionPropTypes {
  if (application.redacted || totalScorecards === null || totalScorecards === 0)
    return null;

  return {
    action: () => {
      window.location.href = appendRedirectUri(
        `/applications/${application.id}?tab=feedback`,
        window.location.href,
        true,
      );
    },
    buttonChild: (
      <IconSpan
        spanText={'View Scorecards'}
        icon={{ name: 'bi-clipboard', className: 'fs-5' }}
        className='text-info'
      />
    ),
  };
}

function viewInterviewKit(application: Application): DropdownActionPropTypes {
  if (application.nextInterviewStageId == null || application.redacted) {
    return null;
  }

  const interviewKitUrl = appendRedirectUri(
    `/applicant_tracking/applications/${application.id}/interview_stages/${application.nextInterviewStageId}?interview_id=last`,
    window.location.href,
    true,
  );

  return {
    action: () => (window.location.href = interviewKitUrl),
    buttonChild: (
      <IconSpan
        spanText={'Interview Kit'}
        icon={{ name: 'bi-credit-card-2-front', className: 'fs-5' }}
        className='text-info'
      />
    ),
  };
}

async function upsertApplicationScorecards(props: {
  applicationId: number;
  setIsLoadingFillScorecard: (isLoading: boolean) => void;
}) {
  props.setIsLoadingFillScorecard(true);

  try {
    await ScorecardService.batchCreate({ applicationId: props.applicationId });
  } finally {
    props.setIsLoadingFillScorecard(false);
  }
}

async function handleFillInScorecard(props: {
  applicationId: number;
  selfPendingScorecards: Scorecard[];
  setIsLoadingFillScorecard: (isLoading: boolean) => void;
}) {
  await upsertApplicationScorecards({
    applicationId: props.applicationId,
    setIsLoadingFillScorecard: props.setIsLoadingFillScorecard,
  });

  if (props.selfPendingScorecards.length === 1) {
    window.location.href = appendRedirectUri(
      `/applicant_tracking/scorecards/${props.selfPendingScorecards[0].id}`,
      window.location.href,
      true,
    );
  } else {
    window.location.href = appendRedirectUri(
      `/applications/${props.applicationId}?tab=feedback&own_scorecards=true`,
      window.location.href,
      true,
    );
  }
}

function fillInScorecard(
  application: Application,
  user: User,
  totalOwnPendingScorecardActions: number,
  isLoadingFillScorecard: boolean,
  setIsLoadingFillScorecard: (isLoading: boolean) => void,
  pendingScorecards?: Scorecard[],
): DropdownActionPropTypes {
  if (pendingScorecards == null || application.redacted) return null;

  const selfPendingScorecards = pendingScorecards.filter(
    (scorecard: Scorecard) => scorecard.userId === user.id,
  );

  if (selfPendingScorecards.length === 0) return null;

  return {
    action: async () =>
      await handleFillInScorecard({
        applicationId: application.id,
        selfPendingScorecards,
        setIsLoadingFillScorecard,
      }),
    buttonChild: (
      <IconSpan
        spanText={
          <FillInScorecard scorecardSize={totalOwnPendingScorecardActions} />
        }
        icon={{ name: 'bi-clipboard-check', className: 'fs-5' }}
        className={'text-info'}
        isLoading={isLoadingFillScorecard}
      />
    ),
  };
}

function addNote(onClick: () => void): DropdownActionPropTypes {
  return {
    action: () => onClick(),
    buttonChild: (
      <IconSpan
        spanText={'Add Note'}
        icon={{ name: 'bi-file-earmark-text', className: 'fs-5' }}
        className='text-info'
      />
    ),
  };
}

function renderRejectCandidate(
  onClick: () => void,
  disabled: boolean,
  application: Application,
): DropdownActionPropTypes {
  if (disabled || application.status == ApplicationStatus.Rejected) return null;

  return {
    action: () => onClick(),
    buttonChild: (
      <IconSpan
        spanText={'Reject Candidate'}
        icon={{ name: 'bi-file-x', className: 'fs-5', topStyle: '2px' }}
        className={'text-info'}
      />
    ),
  };
}

function renderUnrejectCandidate(
  onClick: () => void,
  disabled: boolean,
  application: Application,
): DropdownActionPropTypes {
  if (disabled || application.status != ApplicationStatus.Rejected) return null;

  return {
    action: onClick,
    buttonChild: (
      <IconSpan
        spanText={'Unreject Candidate'}
        icon={{ name: 'bi-file-plus', className: 'fs-5', topStyle: '2px' }}
        className={'text-info'}
      />
    ),
  };
}

function renderShareCNPSSurvey(
  onClick: () => void,
  application: Application,
): DropdownActionPropTypes {
  if (
    application.redacted ||
    !(application.company_info.pulse_enabled && application.enabled_trigger)
  ) {
    return null;
  }

  return {
    action: () => onClick(),
    buttonChild: (
      <IconSpan
        spanText={'Share cNPS Survey'}
        icon={{ name: 'bi-share', className: 'fs-5' }}
        className='text-info'
      />
    ),
  };
}

function renderFillAdHocScorecard(
  onClick: () => void,
): DropdownActionPropTypes {
  return {
    action: () => onClick(),
    buttonChild: (
      <IconSpan
        spanText='Fill in Ad-hoc Scorecard'
        icon={{ name: 'bi-clipboard-plus', className: 'fs-5' }}
        className='text-info'
      />
    ),
  };
}

export function renderPushToHris(
  onClick: () => void,
  redacted: boolean,
  status?: ApplicationStatus | string,
  hrisEmployeeExternalUrl?: string,
  allowedHrisProvider?: HrisProvider,
): DropdownActionPropTypes {
  if (
    !allowedHrisProvider ||
    status?.toString() != ApplicationStatus.Hired.toString() ||
    hrisEmployeeExternalUrl ||
    redacted
  )
    return null;

  return {
    action: () => onClick(),
    buttonChild: (
      <IconSpan
        spanText={`Export to ${getHrisName(allowedHrisProvider)}`}
        icon={{ name: 'bi-journal-plus', className: 'fs-5', topStyle: '2px' }}
        className={'text-info'}
      />
    ),
  };
}

function menuActions(
  application: Application,
  disabled: boolean,
  newApplicationPermitted: boolean,
  sendOfferPermitted: boolean,
  setIsRejectModalOpen: (state: boolean) => void,
  setUnrejectModalOpen: (state: boolean) => void,
  setOfferModalOpen: (state: boolean) => void,
  setIsNoteModalOpen: (state: boolean) => void,
  setIsPushToHrisModalOpen: (state: boolean) => void,
  onShareCNPSSurvey: (flag: boolean) => void,
  setScorecardModalOpen: (state: boolean) => void,
  user: User,
  totalOwnPendingScorecardActions: number,
  offerRequisitionFlowEnabled: boolean,
  pendingScorecards?: ListScorecard,
  totalScorecards?: number,
): DropdownActionPropTypes[] {
  const [isLoadingFillScorecard, setIsLoadingFillScorecard] =
    useState<boolean>(false);

  return [
    renderAddJobApplication(application, newApplicationPermitted),
    renderEditJobApplication(application, disabled),
    renderEditCandidateProfile(application, disabled),
    addNote(() => setIsNoteModalOpen(true)),
    renderFillAdHocScorecard(() => setScorecardModalOpen(true)),
    viewInterviewKit(application),
    renderRejectCandidate(
      () => setIsRejectModalOpen(true),
      disabled,
      application,
    ),
    renderUnrejectCandidate(
      () => setUnrejectModalOpen(true),
      disabled,
      application,
    ),
    viewScorecards(application, totalScorecards),
    fillInScorecard(
      application,
      user,
      totalOwnPendingScorecardActions,
      isLoadingFillScorecard,
      setIsLoadingFillScorecard,
      pendingScorecards?.scorecards,
    ),
    renderShareCNPSSurvey(() => onShareCNPSSurvey(true), application),
    renderPushToHris(
      () => setIsPushToHrisModalOpen(true),
      application.redacted,
      application.status,
      application.hrisEmployee?.externalUrl,
      application.allowedHrisProvider,
    ),
    renderSendOffer(
      () => setOfferModalOpen(true),
      sendOfferPermitted,
      offerRequisitionFlowEnabled,
    ),
    viewOfferRequisition(application.offerRequisitionId),
  ].filter((action) => action != null);
}

export function DropdownActions(props: PropTypes) {
  const [totalScorecards, setTotalScorecards] = useState<number>(null);
  const totalOwnPendingScorecardActions =
    props.application.actionItems.filter((ai) => {
      return (
        ai.hiringMember.userId === props.user.id &&
        ai.intent === 'fill_scorecard' &&
        ai.completed === false
      );
    })?.length || 0;
  const [pendingScorecards, setPendingScorecards] =
    useState<ListScorecard>(null);
  const [offerModalOpen, setOfferModalOpen] = useState<boolean>(false);
  const [isNoteModalOpen, setIsNoteModalOpen] = useState<boolean>(false);
  const [isPushToHrisModalOpen, setIsPushToHrisModalOpen] =
    useState<boolean>(false);
  const [scorecardModalOpen, setScorecardModalOpen] = useState<boolean>(false);

  const handleOfferModalClose = (succeeded: boolean) => {
    setOfferModalOpen(false);
    props.reloadApplication();
    props.onClose(succeeded);
  };

  const handleNoteModalClose = (succeeded: boolean) => {
    setIsNoteModalOpen(false);
    props.onClose(succeeded);
  };

  const handlePushToHrisModalClose = (succeeded: boolean) => {
    setIsPushToHrisModalOpen(false);
    props.onClose(succeeded);
  };

  const onAddNote = async (note: string) => {
    await NoteService.create({
      applicationId: props.application.id,
      note: note,
    });
  };

  const { atsHrisExportV2Enabled } = useFeatureFlags();

  useEffect(() => {
    (async () => {
      Promise.all([
        ScorecardService.list({ applicationId: props.application.id }),
        ScorecardService.list({
          applicationId: props.application.id,
          status: 'pending',
        }),
      ])
        .then(([scorecards, pendingScorecards]) => {
          setPendingScorecards(pendingScorecards);
          setTotalScorecards(scorecards.entriesCount);
        })
        .catch((error) => {
          console.error(error);
        });
    })();
  }, []);

  return (
    <>
      <Dropdown
        buttonClassName={classNames(
          'btn btn-secondary border-blue bg-white',
          styles['action-button'],
          styles['dropdown-button'],
        )}
        buttonIcon={{
          name: 'bi-three-dots-vertical',
          className: 'text-info',
          topStyle: '-2px',
        }}
        menuPlacementClass={'dropdown-menu-end'}
        menuActions={menuActions(
          props.application,
          props.disabled,
          props.newApplicationPermitted,
          props.sendOfferPermitted,
          props.setIsRejectModalOpen,
          props.setUnrejectModalOpen,
          setOfferModalOpen,
          setIsNoteModalOpen,
          setIsPushToHrisModalOpen,
          props.onShareCNPSSurvey,
          setScorecardModalOpen,
          props.user,
          totalOwnPendingScorecardActions,
          props.offerRequisitionFlowEnabled,
          pendingScorecards,
          totalScorecards,
        )}
      />
      <SendOfferSwitch
        atsSignableOfferEnabled={props.atsSignableOfferEnabled}
        applicationId={props.application.id}
        hasEmailConnected={props.user.hasEmailConnected}
        isOpen={offerModalOpen}
        to={props.application.candidate.email}
        offerRequisitionFlowEnabled={props.offerRequisitionFlowEnabled}
        setAlert={props.setAlert}
        onClose={handleOfferModalClose}
      />
      <NoteModal
        mode='Add'
        onSubmit={onAddNote}
        isOpen={isNoteModalOpen}
        onClose={handleNoteModalClose}
      />
      {atsHrisExportV2Enabled ? (
        <EditablePushToHrisModal
          applicationIds={[props.application.id]}
          applicationId={props.application.id}
          allowedHrisProvider={props.application.allowedHrisProvider}
          isOpen={isPushToHrisModalOpen}
          onClose={handlePushToHrisModalClose}
          setAlert={props.setAlert}
        />
      ) : (
        <LegacyEditablePushToHrisModal
          applicationIds={[props.application.id]}
          applicationId={props.application.id}
          allowedHrisProvider={props.application.allowedHrisProvider}
          isOpen={isPushToHrisModalOpen}
          onClose={handlePushToHrisModalClose}
          setAlert={props.setAlert}
        />
      )}
      <AddScorecardModal
        application={props.application}
        isOpen={scorecardModalOpen}
        onClose={() => setScorecardModalOpen(false)}
      />
    </>
  );
}
